import { Redirect } from 'react-router-dom';

import type { UserPermissions } from '@/types/directory/User';
import type { ReactNode } from 'react';

import { useGetUserPermissions } from '@/api/directory';
import { RouteName } from '@/navigation/types';

interface PermissionGuardProps {
    hasPermission: boolean | undefined;
    children: ReactNode;
}

/**
 * Redirects to the default route when the user lacks the required permission.
 * Renders nothing while permissions are still loading (undefined).
 */
export const PermissionGuard = ({ hasPermission, children }: PermissionGuardProps) => {
    if (hasPermission === undefined) {
        return null;
    }

    if (!hasPermission) {
        return <Redirect to={RouteName.Evaluation360} />;
    }

    return <>{children}</>;
};

type WithPermissionProps = {
    children: ReactNode;
} & (
    | { permissionKey: keyof UserPermissions; anyOfPermissionKeys?: never }
    | { permissionKey?: never; anyOfPermissionKeys: (keyof UserPermissions)[] }
);

const resolveHasPermission = (
    permissions: UserPermissions | undefined,
    permissionKey: keyof UserPermissions | undefined,
    anyOfPermissionKeys: (keyof UserPermissions)[] | undefined,
): boolean | undefined => {
    if (!permissions) {
        return undefined;
    }

    if (anyOfPermissionKeys) {
        return anyOfPermissionKeys.some((key) => permissions[key]);
    }

    return Boolean(permissionKey && permissions[permissionKey]);
};

export const WithPermission = ({
    permissionKey,
    anyOfPermissionKeys,
    children,
}: WithPermissionProps) => {
    const { data: permissions, isError } = useGetUserPermissions();

    // A failed permissions fetch must degrade to the default route, not to a
    // permanently blank page (`undefined` renders null while loading only).
    if (isError) {
        return <PermissionGuard hasPermission={false}>{children}</PermissionGuard>;
    }

    return (
        <PermissionGuard
            hasPermission={resolveHasPermission(permissions, permissionKey, anyOfPermissionKeys)}
        >
            {children}
        </PermissionGuard>
    );
};
